import React, { useState, useEffect } from 'react';
import { Users, Plus, Edit, Trash2, Filter, Search, X, UserPlus, Eye } from 'lucide-react';
import { FilterBuilder } from './FilterBuilder';
import { groupsApi, Group } from '@/services/groupsApi';
import type { Filter } from '@/services/groupsApi';
import { membersAPI, Member } from '@/services/api/members';

import { useAppTranslation } from '@/contexts/TranslationContext';
interface GroupsPageProps {
  onSendMessage?: (groupId: string) => void;
}

interface GroupFormData {
  name: string;
  description: string;
  filters: Filter[];
  include_record_types?: string[];
}

export const GroupsPage: React.FC<GroupsPageProps> = ({ onSendMessage }) => {
  const { t } = useAppTranslation();
  const [groups, setGroups] = useState<Group[]>([]);
  const [loading, setLoading] = useState(true);
  const [showCreateForm, setShowCreateForm] = useState(false);
  const [editingGroup, setEditingGroup] = useState<Group | null>(null);
  const [formData, setFormData] = useState<GroupFormData>({
    name: '',
    description: '',
    filters: [] as Filter[]
  });

  // Member search and selection
  const [memberSearchQuery, setMemberSearchQuery] = useState('');
  const [searchResults, setSearchResults] = useState<Member[]>([]);
  const [selectedMembers, setSelectedMembers] = useState<Member[]>([]);
  const [searchingMembers, setSearchingMembers] = useState(false);

  // View members modal state
  const [viewMembersGroup, setViewMembersGroup] = useState<Group | null>(null);
  const [viewMembersList, setViewMembersList] = useState<Member[]>([]);
  const [viewMembersLoading, setViewMembersLoading] = useState(false);
  const [viewMembersError, setViewMembersError] = useState<string | null>(null);

  const handleViewMembers = async (group: Group) => {
    setViewMembersGroup(group);
    setViewMembersList([]);
    setViewMembersError(null);
    setViewMembersLoading(true);
    try {
      const data = await groupsApi.getGroupMembers(group.id, { limit: 500 });
      setViewMembersList(data || []);
    } catch (err: any) {
      setViewMembersError(err?.message || 'Failed to load members');
    } finally {
      setViewMembersLoading(false);
    }
  };

  useEffect(() => {
    loadGroups();
  }, []);

  const loadGroups = async () => {
    try {
      setLoading(true);
      const data = await groupsApi.listGroups();
      setGroups(data);
    } catch (error) {
      console.error('Failed to load groups:', error);
    } finally {
      setLoading(false);
    }
  };

  const handleCreateGroup = async () => {
    try {
      // Combine filter builder filters with manually selected members
      let finalFilters = [...formData.filters];

      // If specific members are selected, create an "id IN [...]" filter
      if (selectedMembers.length > 0) {
        const memberIds = selectedMembers.map(m => m.id);
        const memberFilter = {
          field: 'id',
          operator: 'IN' as const,
          value: memberIds,
          logic_operator: (finalFilters.length > 0 ? 'AND' : 'AND') as 'AND' | 'OR'
        };
        finalFilters.push(memberFilter);
      }

      if (editingGroup) {
        // Update existing group
        await groupsApi.updateGroup(editingGroup.id, {
          name: formData.name,
          description: formData.description,
          filters: finalFilters
        });
      } else {
        // Create new group
        await groupsApi.createGroup({
          name: formData.name,
          description: formData.description,
          organization_id: parseInt(localStorage.getItem('active_cbo_id') || '1'),
          filters: finalFilters,
          include_record_types: (formData as any).include_record_types || ['member'],
        });
      }

      // Reset form and close modal
      setShowCreateForm(false);
      setEditingGroup(null);
      setFormData({ name: '', description: '', filters: [] });
      setSelectedMembers([]);
      setMemberSearchQuery('');
      setSearchResults([]);

      // Reload groups
      loadGroups();
    } catch (error) {
      console.error('Failed to save group:', error);
      alert(`Failed to ${editingGroup ? 'update' : 'create'} group. Please try again.`);
    }
  };

  const handleEditGroup = async (group: Group) => {
    try {
      // Fetch the full group details including filters (list endpoint doesn't return filters)
      const fullGroup = await groupsApi.getGroup(group.id);
      setEditingGroup(fullGroup);

      // Separate member ID filters from other filters
      const filters = fullGroup.filters || [];
      const memberIdFilter = filters.find(f => f.field === 'id' && f.operator === 'IN');
      const otherFilters = filters.filter(f => !(f.field === 'id' && f.operator === 'IN'));

      // If there's a member ID filter, load those members
      if (memberIdFilter && Array.isArray(memberIdFilter.value) && memberIdFilter.value.length > 0) {
        try {
          // Fetch the selected members by their IDs
          const memberPromises = memberIdFilter.value.map(async (id: number) => {
            try {
              return await membersAPI.getMember(id);
            } catch {
              return null;
            }
          });
          const members = (await Promise.all(memberPromises)).filter(m => m !== null) as Member[];
          setSelectedMembers(members);
        } catch (error) {
          console.error('Failed to load selected members:', error);
          setSelectedMembers([]);
        }
      } else {
        setSelectedMembers([]);
      }

      setFormData({
        name: fullGroup.name,
        description: fullGroup.description || '',
        filters: otherFilters
      });
      setShowCreateForm(true);
    } catch (error) {
      console.error('Failed to load group details:', error);
      alert('Failed to load group details. Please try again.');
    }
  };

  const handleMemberSearch = async (query: string) => {
    setMemberSearchQuery(query);

    if (query.length < 2) {
      setSearchResults([]);
      return;
    }

    try {
      setSearchingMembers(true);
      const response = await membersAPI.listMembers({ search: query, page_size: 20 });
      setSearchResults(response.members);
    } catch (error) {
      console.error('Failed to search members:', error);
      setSearchResults([]);
    } finally {
      setSearchingMembers(false);
    }
  };

  const handleAddMember = (member: Member) => {
    if (!selectedMembers.find(m => m.id === member.id)) {
      setSelectedMembers([...selectedMembers, member]);
    }
    setMemberSearchQuery('');
    setSearchResults([]);
  };

  const handleRemoveMember = (memberId: number) => {
    setSelectedMembers(selectedMembers.filter(m => m.id !== memberId));
  };

  const handleDeleteGroup = async (groupId: string) => {
    if (!confirm('Are you sure you want to delete this group?')) return;

    try {
      await groupsApi.deleteGroup(groupId);
      loadGroups();
    } catch (error) {
      console.error('Failed to delete group:', error);
    }
  };

  return (
    <div className="h-full flex flex-col bg-gray-50 dark:bg-gray-700">
      {/* Toolbar */}
      <div className="bg-surface border-b border-v2border px-6 py-4">
        <div className="flex flex-wrap items-center justify-end gap-2">
          <button
            onClick={() => setShowCreateForm(true)}
            className="flex items-center gap-2 px-4 py-2 bg-blue-600 text-white rounded-lg hover:bg-blue-700 transition-colors"
          >
            <Plus className="w-5 h-5" />
            {t('groups.create_group', 'Create Group')}
          </button>
        </div>
      </div>

      {/* Content */}
      <div className="flex-1 overflow-y-auto p-6">
        {loading ? (
          <div className="flex items-center justify-center h-full">
            <div className="text-center">
              <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-blue-600 mx-auto"></div>
              <p className="mt-4 text-gray-600 dark:text-gray-300">{t('groups.loading_groups', 'Loading groups...')}</p>
            </div>
          </div>
        ) : groups.length === 0 ? (
          <div className="flex flex-col items-center justify-center h-full text-center">
            <Users className="w-16 h-16 text-gray-400 mb-4" />
            <h3 className="text-xl font-semibold text-gray-900 mb-2">{t('groups.no_groups_yet', 'No groups yet')}</h3>
            <p className="text-gray-600 mb-6 max-w-md">
              {t('groups.create_your_first_member_group', 'Create your first member group to start sending targeted communications')}
            </p>
            <button
              onClick={() => setShowCreateForm(true)}
              className="flex items-center gap-2 px-6 py-3 bg-blue-600 text-white rounded-lg hover:bg-blue-700 transition-colors"
            >
              <Plus className="w-5 h-5" />
              {t('groups.create_your_first_group', 'Create Your First Group')}
            </button>
          </div>
        ) : (
          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6">
            {groups.map((group) => (
              <div
                key={group.id}
                className="bg-white dark:bg-gray-800 rounded-lg border border-gray-200 p-6 hover:shadow-lg transition-shadow"
              >
                <div className="flex items-start justify-between mb-4">
                  <div className="flex-1">
                    <h3 className="text-lg font-semibold text-gray-900 mb-1">
                      {group.name}
                    </h3>
                    <p className="text-sm text-gray-600 line-clamp-2">
                      {group.description}
                    </p>
                  </div>
                </div>

                <div className="flex items-center gap-2 text-sm text-gray-600 mb-4">
                  <Users className="w-4 h-4" />
                  <span className="font-medium">{group.member_count}</span>
                  <span>members</span>
                </div>

                <div className="flex items-center gap-2">
                  {/* "Send Message" removed — was a non-functioning shortcut.
                      Compose campaigns/engagements directly from the
                      Engagements / Campaigns pages instead. */}
                  <button
                    onClick={() => handleViewMembers(group)}
                    className="flex-1 flex items-center justify-center gap-2 px-3 py-2 bg-blue-50 text-blue-700 rounded-lg hover:bg-blue-100 transition-colors"
                    title="View members in this group"
                  >
                    <Eye className="w-4 h-4" />
                    View members
                  </button>
                  <button
                    onClick={() => handleEditGroup(group)}
                    className="flex-1 flex items-center justify-center gap-2 px-3 py-2 bg-gray-50 text-gray-700 rounded-lg hover:bg-gray-100 transition-colors"
                    title={t('groups.edit_group', 'Edit group')}
                  >
                    <Edit className="w-4 h-4" />
                    {t('groups.edit_group', 'Edit group')}
                  </button>
                  <button
                    onClick={() => handleDeleteGroup(group.id)}
                    className="px-3 py-2 text-red-600 hover:bg-red-50 rounded-lg transition-colors"
                  >
                    <Trash2 className="w-4 h-4" />
                  </button>
                </div>
              </div>
            ))}
          </div>
        )}
      </div>

      {/* Create/Edit Group Modal */}
      {showCreateForm && (
        <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50 p-4">
          <div className="bg-white dark:bg-gray-800 rounded-lg max-w-2xl w-full max-h-[90vh] overflow-y-auto">
            <div className="p-6 border-b border-gray-200 dark:border-gray-700">
              <h2 className="text-xl font-bold text-gray-900 dark:text-white">
                {editingGroup ? 'Edit Group' : 'Create New Group'}
              </h2>
            </div>

            <div className="p-6 space-y-4">
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-2">
                  {t('groups.group_name', 'Group Name *')}
                </label>
                <input
                  type="text"
                  value={formData.name}
                  onChange={(e) => setFormData({ ...formData, name: e.target.value })}
                  className="w-full px-3 py-2 border border-gray-300 dark:border-gray-600 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent"
                  placeholder="e.g., High-Risk Patients, Spanish Speakers"
                />
              </div>

              <div>
                <label className="block text-sm font-medium text-gray-700 mb-2">
                  {t('groups.description', 'Description')}
                </label>
                <textarea
                  value={formData.description}
                  onChange={(e) => setFormData({ ...formData, description: e.target.value })}
                  rows={3}
                  className="w-full px-3 py-2 border border-gray-300 dark:border-gray-600 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent"
                  placeholder={t('groups.describe_the_purpose_of_this', 'Describe the purpose of this group')}
                />
              </div>

              <div>
                <label className="block text-sm font-medium text-gray-700 mb-2">
                  Audience Type
                </label>
                <div className="flex gap-2">
                  {[
                    { value: 'member', label: 'Members Only' },
                    { value: 'contact', label: 'Contacts Only' },
                    { value: 'both', label: 'Members & Contacts' },
                  ].map((opt) => (
                    <button
                      key={opt.value}
                      type="button"
                      onClick={() => setFormData({
                        ...formData,
                        include_record_types: opt.value === 'both' ? ['member', 'contact'] : [opt.value]
                      } as any)}
                      className={`px-3 py-2 text-sm rounded-lg border transition-all ${
                        (formData as any).include_record_types?.includes(opt.value) ||
                        (opt.value === 'both' && (formData as any).include_record_types?.length === 2) ||
                        (!((formData as any).include_record_types) && opt.value === 'member')
                          ? 'border-blue-500 bg-blue-50 text-blue-700 font-medium'
                          : 'border-gray-200 text-gray-500 hover:bg-gray-50'
                      }`}
                    >
                      {opt.label}
                    </button>
                  ))}
                </div>
              </div>

              <div>
                <label className="block text-sm font-medium text-gray-700 mb-2 flex items-center gap-2">
                  <Filter className="w-4 h-4" />
                  Member Filters (Optional)
                </label>
                <p className="text-sm text-gray-600 mb-3">
                  {t('groups.add_filters_to_automatically_include', 'Add filters to automatically include members who match criteria')}
                </p>
                <FilterBuilder
                  filters={formData.filters}
                  onChange={(filters) => setFormData({ ...formData, filters })}
                />
              </div>

              {/* Manual Member Selection */}
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-2 flex items-center gap-2">
                  <UserPlus className="w-4 h-4" />
                  Add Specific Members (Optional)
                </label>
                <p className="text-sm text-gray-600 mb-3">
                  {t('groups.manually_select_individual_members_to', 'Manually select individual members to add to this group')}
                </p>

                {/* Member Search */}
                <div className="relative mb-3">
                  <div className="relative">
                    <Search className="absolute left-3 top-1/2 transform -translate-y-1/2 w-4 h-4 text-gray-400" />
                    <input
                      type="text"
                      value={memberSearchQuery}
                      onChange={(e) => handleMemberSearch(e.target.value)}
                      placeholder={t('groups.search_members_by_name_email', 'Search members by name, email, or phone...')}
                      className="w-full pl-10 pr-3 py-2 border border-gray-300 dark:border-gray-600 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent text-sm"
                    />
                  </div>

                  {/* Search Results Dropdown */}
                  {searchResults.length > 0 && (
                    <div className="absolute z-10 w-full mt-1 bg-white border border-gray-300 dark:border-gray-600 rounded-lg shadow-lg max-h-60 overflow-y-auto">
                      {searchingMembers ? (
                        <div className="p-3 text-center text-sm text-gray-600 dark:text-gray-300">
                          {t('groups.searching', 'Searching...')}
                        </div>
                      ) : (
                        searchResults.map((member) => (
                          <button
                            key={member.id}
                            onClick={() => handleAddMember(member)}
                            className="w-full text-left px-3 py-2 hover:bg-blue-50 border-b border-gray-100 last:border-b-0 transition-colors"
                          >
                            <div className="text-sm font-medium text-gray-900 dark:text-white">
                              {member.first_name} {member.last_name}
                            </div>
                            <div className="text-xs text-gray-600 dark:text-gray-300">
                              {member.email || member.phone_no || member.phone_number || 'No contact info'}
                            </div>
                          </button>
                        ))
                      )}
                    </div>
                  )}
                </div>

                {/* Selected Members */}
                {selectedMembers.length > 0 && (
                  <div className="space-y-2">
                    <p className="text-xs font-medium text-gray-700 dark:text-gray-300">
                      Selected Members ({selectedMembers.length})
                    </p>
                    <div className="max-h-40 overflow-y-auto space-y-1">
                      {selectedMembers.map((member) => (
                        <div
                          key={member.id}
                          className="flex items-center justify-between bg-blue-50 border border-blue-200 rounded-lg px-3 py-2"
                        >
                          <div className="flex-1 min-w-0">
                            <div className="text-sm font-medium text-gray-900 truncate">
                              {member.first_name} {member.last_name}
                            </div>
                            <div className="text-xs text-gray-600 truncate">
                              {member.email || member.phone_no || member.phone_number}
                            </div>
                          </div>
                          <button
                            onClick={() => handleRemoveMember(member.id)}
                            className="ml-2 p-1 text-red-600 hover:bg-red-100 rounded transition-colors flex-shrink-0"
                            title={t('groups.remove_member', 'Remove member')}
                          >
                            <X className="w-4 h-4" />
                          </button>
                        </div>
                      ))}
                    </div>
                  </div>
                )}
              </div>
            </div>

            <div className="p-6 border-t border-gray-200 flex items-center justify-end gap-3">
              <button
                onClick={() => {
                  setShowCreateForm(false);
                  setEditingGroup(null);
                  setFormData({ name: '', description: '', filters: [] });
                  setSelectedMembers([]);
                  setMemberSearchQuery('');
                  setSearchResults([]);
                }}
                className="px-4 py-2 text-gray-700 hover:bg-gray-100 rounded-lg transition-colors"
              >
                {t('groups.cancel', 'Cancel')}
              </button>
              <button
                onClick={handleCreateGroup}
                disabled={!formData.name}
                className="px-4 py-2 bg-blue-600 text-white rounded-lg hover:bg-blue-700 transition-colors disabled:opacity-50 disabled:cursor-not-allowed"
              >
                {editingGroup ? 'Update Group' : 'Create Group'}
              </button>
            </div>
          </div>
        </div>
      )}

      {/* View members modal */}
      {viewMembersGroup && (
        <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50 p-4">
          <div className="bg-white dark:bg-gray-800 rounded-lg max-w-3xl w-full max-h-[85vh] overflow-hidden flex flex-col">
            <div className="p-5 border-b border-gray-200 dark:border-gray-700 flex items-center justify-between">
              <div>
                <h2 className="text-lg font-semibold text-gray-900 dark:text-white">
                  Members in "{viewMembersGroup.name}"
                </h2>
                <p className="text-xs text-gray-500 mt-1">
                  Group member_count: {viewMembersGroup.member_count} ·
                  Loaded from filter rules
                </p>
              </div>
              <button
                onClick={() => { setViewMembersGroup(null); setViewMembersList([]); setViewMembersError(null); }}
                className="p-1.5 text-gray-400 hover:text-gray-600 hover:bg-gray-100 dark:hover:bg-gray-700 rounded"
              >
                <X className="w-5 h-5" />
              </button>
            </div>
            <div className="flex-1 overflow-auto p-5">
              {viewMembersLoading ? (
                <div className="text-center text-gray-500 py-8">Loading…</div>
              ) : viewMembersError ? (
                <div className="text-center text-red-600 py-8">{viewMembersError}</div>
              ) : viewMembersList.length === 0 ? (
                <div className="text-center text-gray-500 py-8">
                  No members matched the filter rules.
                </div>
              ) : (
                <table className="w-full text-sm">
                  <thead className="text-xs uppercase text-gray-500 border-b">
                    <tr>
                      <th className="text-left px-3 py-2">Name</th>
                      <th className="text-left px-3 py-2">Email</th>
                      <th className="text-left px-3 py-2">Phone</th>
                      <th className="text-left px-3 py-2">Language</th>
                    </tr>
                  </thead>
                  <tbody className="divide-y divide-gray-100 dark:divide-gray-800">
                    {viewMembersList.map((m: any) => (
                      <tr key={m.id} className="hover:bg-gray-50 dark:hover:bg-gray-700/30">
                        <td className="px-3 py-2 text-gray-900 dark:text-gray-100">
                          {(m.first_name || '') + ' ' + (m.last_name || '')}
                        </td>
                        <td className="px-3 py-2 text-gray-600 dark:text-gray-300">{m.email || '—'}</td>
                        <td className="px-3 py-2 text-gray-600 dark:text-gray-300">{m.phone || '—'}</td>
                        <td className="px-3 py-2 text-gray-600 dark:text-gray-300">{m.preferred_language || '—'}</td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              )}
            </div>
          </div>
        </div>
      )}
    </div>
  );
};
